// ----- Phase 5.X-background-validation-ui — useValidationStatus hook -----
//
// Polls GET /api/v1/submissions/:id/validation-status every 5 seconds when
// the `continuousValidation` setting is enabled for the calling user. When
// the setting is off the hook makes a single fetch (no polling overhead)
// so viewers without the feature see a static last-known score.
//
// Design notes:
//   - Poll interval: 5 s (per prompt hard guardrail — not more frequent).
//   - The in-flight `status: 'running'` is driven by the server-side
//     in-memory flag (see continuousValidationService.ts). After a server
//     restart the flag is empty so polling clients briefly see `idle`
//     even if a run is mid-flight; that's acceptable.
//   - Unmount cleanup: the interval is cleared via `useEffect` cleanup.
//   - The `useSetting('continuousValidation')` call shares the module-level
//     settings cache — no extra fetch if another component already loaded it.

import { useEffect, useRef, useState } from 'react';
import { v2 } from '@/api/v2Client';
import type { ValidationCurrencyWire } from '@/api/v2Client';
import { useSetting } from './useSetting';

const POLL_INTERVAL_MS = 5_000;

export interface UseValidationStatusResult {
  readonly status: 'idle' | 'running' | 'complete';
  readonly errorCount: number;
  readonly warningCount: number;
  readonly infoCount: number;
  readonly lastRunAt: string | null;
  /**
   * ISS-017 — WHY the server considers the run current or not (T17-D43).
   * `status === 'idle'` conflates "never run" with "a run exists but is
   * stale / per-document only"; surfaces must tell those apart so they
   * never claim "no run yet" while another surface shows a run's results.
   * Absent on an older backend.
   */
  readonly currency: ValidationCurrencyWire | undefined;
  /** True while the very first fetch is in-flight (no data yet). */
  readonly loading: boolean;
  /** Non-null when the last fetch failed. */
  readonly error: string | null;
}

const INITIAL: UseValidationStatusResult = {
  status: 'idle',
  errorCount: 0,
  warningCount: 0,
  infoCount: 0,
  lastRunAt: null,
  currency: undefined,
  loading: true,
  error: null,
};

export function useValidationStatus(submissionId: string | null | undefined): UseValidationStatusResult {
  const { value: continuousValidationOn, loading: settingLoading } =
    // T-17 (D6) — read for THIS submission, so a submission-level override applies.
    useSetting<boolean>('continuousValidation', { submissionId });

  const [result, setResult] = useState<UseValidationStatusResult>(INITIAL);
  // Track the latest submissionId for the async callback without adding it
  // to the interval's dependency list (avoids restarting the interval on
  // every render when submissionId is stable).
  const submissionIdRef = useRef(submissionId);
  submissionIdRef.current = submissionId;

  useEffect(() => {
    if (settingLoading) return; // wait until we know the setting value
    const sid = submissionIdRef.current;
    if (!sid) {
      setResult({ ...INITIAL, loading: false });
      return;
    }

    let cancelled = false;

    async function fetch(): Promise<void> {
      const s = submissionIdRef.current;
      if (!s || cancelled) return;
      try {
        const res = await v2.submissions.getValidationStatus(s);
        if (cancelled) return;
        setResult({
          status: res.status,
          errorCount: res.errorCount,
          warningCount: res.warningCount,
          infoCount: res.infoCount,
          lastRunAt: res.lastRunAt,
          currency: res.currency,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setResult((prev) => ({ ...prev, loading: false, error: msg }));
      }
    }

    // Initial fetch on mount / setting-resolved.
    void fetch();

    // Only poll when continuousValidation is on.
    if (!continuousValidationOn) return;

    const intervalId = globalThis.setInterval(() => {
      void fetch();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      globalThis.clearInterval(intervalId);
    };
  }, [settingLoading, continuousValidationOn, submissionId]);

  return result;
}
